import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, DollarSign, Users, Share2, CheckCircle2 } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AffiliateApply() {
  const nav = useNavigate();
  const { user, token, authHeader } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [requestedCode, setRequestedCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null); // affiliate object on success

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!paymentDetails.trim()) { setErr("Payment details are required."); return; }
    if (!user && (!name || !email || password.length < 8)) {
      setErr("Name, email and a password of 8+ characters are required.");
      return;
    }
    setLoading(true);
    try {
      const body = {
        payment_method: paymentMethod,
        payment_details: paymentDetails.trim(),
      };
      if (requestedCode.trim()) body.requested_code = requestedCode.trim().toUpperCase();
      if (!user) {
        body.name = name.trim();
        body.email = email.trim();
        body.password = password;
      }
      const { data } = await axios.post(`${API}/affiliate/signup`, body, {
        headers: user ? authHeader() : undefined,
      });
      setOk(data.affiliate);
      // If a fresh account was created inline, adopt the returned token
      if (data.token && !token) {
        localStorage.setItem("stageletics_token", data.token);
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (ok) {
    return (
      <div data-testid="affiliate-apply-success" className="bg-[#050505] text-white pt-32 min-h-screen">
        <div className="px-6 md:px-12 lg:px-24 pb-24 max-w-2xl mx-auto text-center">
          <CheckCircle2 className="w-16 h-16 text-[#E8A851] mx-auto mb-6" />
          <p className="eyebrow mb-4">Application received</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-4">
            Thanks, {ok.name?.split(" ")[0]}.
          </h1>
          <p className="text-zinc-300 mb-8 leading-relaxed">
            Your affiliate account is <span className="text-[#E8A851]">pending review</span>. We'll email you at{" "}
            <span className="text-white">{ok.email}</span> once approved (usually within 1 business day).
          </p>
          <div className="border border-white/10 bg-white/[0.02] p-6 text-left">
            <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Your affiliate code</p>
            <p className="font-mono text-2xl text-[#E8A851]">{ok.code}</p>
            <p className="text-xs text-zinc-500 mt-3">
              Once approved, your ref link will be{" "}
              <span className="text-white">stageletics.com/?ref={ok.code}</span>
            </p>
          </div>
          <button
            data-testid="affiliate-apply-success-continue"
            onClick={() => nav("/affiliate/dashboard")}
            className="btn-primary mt-10 inline-flex"
          >
            Go to affiliate dashboard <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="affiliate-apply-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <p className="eyebrow mb-4">Affiliate program</p>
          <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tighter leading-[1.05]">
            Recommend Stageletics.
            <br />
            <span className="text-[#E8A851]">Earn on every sale.</span>
          </h1>
        </div>

        {/* Value props */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {[
            { icon: DollarSign, label: "5% on first 10 sales, 10% after", sub: "Default tier. Admin can set a custom rate." },
            { icon: Users, label: "30-day cookie window", sub: "Once someone clicks your link, you're credited for a month." },
            { icon: Share2, label: "Share tools built in", sub: "Link, QR, and pre-written snippets on your dashboard." },
          ].map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={v.label} className="border border-white/10 bg-white/[0.02] p-6">
                <Icon className="w-6 h-6 text-[#E8A851] mb-4" />
                <p className="font-display text-lg font-medium">{v.label}</p>
                <p className="text-xs text-zinc-500 mt-2 leading-relaxed">{v.sub}</p>
              </div>
            );
          })}
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <div className="lg:col-span-2">
            <p className="eyebrow mb-4">How it works</p>
            <ol className="space-y-4 text-sm text-zinc-400 leading-relaxed">
              <li>1. Apply below — takes 60 seconds.</li>
              <li>2. Admin approves your account within 1 business day.</li>
              <li>3. You get a personal ref link + share tools.</li>
              <li>4. Share on socials, in your newsletter, or with your worship network.</li>
              <li>5. Get paid manually (PayPal, Venmo, or check).</li>
            </ol>
          </div>

          <form
            data-testid="affiliate-apply-form"
            onSubmit={submit}
            className="lg:col-span-3 border border-white/10 bg-white/[0.02] p-8 md:p-10 space-y-7"
          >
            <p className="eyebrow">Apply for affiliate</p>

            {!user && (
              <>
                <div>
                  <label className="eyebrow mb-2 block">Full name</label>
                  <input
                    data-testid="affiliate-name-input"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-3 focus:outline-none"
                    placeholder="Sarah Johnson"
                  />
                </div>
                <div>
                  <label className="eyebrow mb-2 block">Email</label>
                  <input
                    data-testid="affiliate-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-3 focus:outline-none"
                    placeholder="sarah@yourchurch.org"
                  />
                </div>
                <div>
                  <label className="eyebrow mb-2 block">Password (min 8)</label>
                  <input
                    data-testid="affiliate-password-input"
                    type="password"
                    minLength={8}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-3 focus:outline-none"
                  />
                </div>
              </>
            )}

            {user && (
              <div className="border border-white/10 bg-white/[0.02] p-4 text-sm text-zinc-400">
                Applying as <span className="text-white">{user.email}</span>
              </div>
            )}

            <div>
              <label className="eyebrow mb-2 block">Preferred ref code (optional)</label>
              <input
                data-testid="affiliate-code-input"
                type="text"
                value={requestedCode}
                onChange={(e) => setRequestedCode(e.target.value.toUpperCase())}
                placeholder="SARAH20"
                pattern="[A-Z0-9_\\-]{3,30}"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-3 focus:outline-none uppercase tracking-wider"
              />
              <p className="text-xs text-zinc-500 mt-2">3-30 chars, A-Z 0-9 _ -. We'll assign one if taken.</p>
            </div>
            <div>
              <label className="eyebrow mb-2 block">How should we pay you?</label>
              <select
                data-testid="affiliate-payment-method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full bg-black/40 border border-white/20 text-white px-3 py-3 focus:outline-none"
              >
                <option value="paypal">PayPal</option>
                <option value="venmo">Venmo</option>
                <option value="zelle">Zelle</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="eyebrow mb-2 block">
                {paymentMethod === "check" ? "Mailing address" : "Handle / email"}
              </label>
              <input
                data-testid="affiliate-payment-details"
                type="text"
                required
                value={paymentDetails}
                onChange={(e) => setPaymentDetails(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#E8A851] text-white px-0 py-3 focus:outline-none"
                placeholder={paymentMethod === "check" ? "Full mailing address" : "sarah@paypal.me"}
              />
            </div>

            {err && <p data-testid="affiliate-apply-error" className="text-sm text-red-400">{err}</p>}

            <button
              data-testid="affiliate-apply-submit"
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-50"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Apply <ArrowRight className="w-4 h-4" /></>}
            </button>

            {!user && (
              <p className="text-xs text-zinc-500 text-center">
                Already have an account?{" "}
                <Link to="/login?next=/affiliate" className="text-white underline">Sign in</Link>{" "}
                first, then apply.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
